import { BoltIcon, FunnelIcon } from '@heroicons/react/24/outline';
import type {
  ActiveFilter,
  FilterDefinition,
  FilterGeneratorContext,
  FilterIcon,
  FilterSuggestion,
  SerializedFilter
} from '@oysterjs/ui/search-filter';
import { BaseDateFilter, BaseSearchFilter, getSinceDate } from '~/api/data/filter-base';
import {
  TriggerDefinitionStatus,
  TriggerEventState,
  TriggerEventType,
  type TriggerDefinitionFieldsFragment,
  type TriggerEventListFieldsFragment
} from '~/api/graphql';
import { EVENT_TYPE_LABELS } from './trigger-utils';

export type TriggerFilter = 'search' | 'status' | 'type';
export type TriggerExecutionFilter = 'search' | 'state' | 'type' | 'date';

interface FilterOption {
  value: string;
  label: string;
}

const EVENT_TYPES = [
  TriggerEventType.EmailReceived,
  TriggerEventType.ScheduleDue,
  TriggerEventType.CallAnalyzed
];

/** Suggests one fixed option per match, e.g. "Status is Paused". */
class OptionFilter<T extends string> implements FilterDefinition<T> {
  readonly priority = 10;
  readonly isUnique = true;

  constructor(
    readonly type: T,
    readonly icon: FilterIcon,
    private readonly displayLabel: string,
    private readonly options: FilterOption[]
  ) {}

  generate({ query }: FilterGeneratorContext<T>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    const keyword = this.displayLabel.toLowerCase();
    return this.options
      .filter((o) => o.label.toLowerCase().includes(q) || keyword.includes(q))
      .map((o) => ({ value: o.value, displayLabel: this.displayLabel, displayValue: o.label }));
  }

  deserialize(s: SerializedFilter<T>): ActiveFilter<T> {
    return {
      id: `${this.type}-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: this.displayLabel,
      displayValue: s.displayValue
    };
  }
}

const matchesSearch = (text: string, filter: ActiveFilter<string>) =>
  text.toLowerCase().includes(filter.value.toLowerCase());

export const createTriggerFilterDefinitions = (): FilterDefinition<TriggerFilter>[] => [
  new OptionFilter<TriggerFilter>('status', FunnelIcon, 'Status is', [
    { value: TriggerDefinitionStatus.Active, label: 'Active' },
    { value: TriggerDefinitionStatus.Paused, label: 'Paused' }
  ]),
  new OptionFilter<TriggerFilter>(
    'type',
    BoltIcon,
    'Type is',
    EVENT_TYPES.map((t) => ({ value: t, label: EVENT_TYPE_LABELS[t] }))
  ),
  new BaseSearchFilter<TriggerFilter>('search')
];

/** `searchText` supplies the row's visible text, which depends on agency and preset context. */
export function filterTriggers(
  triggers: TriggerDefinitionFieldsFragment[],
  activeFilters: ActiveFilter<TriggerFilter>[],
  searchText: (trigger: TriggerDefinitionFieldsFragment) => string
): TriggerDefinitionFieldsFragment[] {
  if (activeFilters.length === 0) return triggers;
  return triggers.filter((trigger) =>
    activeFilters.every((filter) => {
      switch (filter.type) {
        case 'status':
          return trigger.status === filter.value;
        case 'type':
          return trigger.eventMatcher.eventType === filter.value;
        case 'search':
          return matchesSearch(searchText(trigger), filter);
        default:
          return true;
      }
    })
  );
}

export const createTriggerExecutionFilterDefinitions =
  (): FilterDefinition<TriggerExecutionFilter>[] => [
    new OptionFilter<TriggerExecutionFilter>('state', FunnelIcon, 'State is', [
      { value: TriggerEventState.Evaluated, label: 'Evaluated' },
      { value: TriggerEventState.Pending, label: 'Pending' },
      { value: TriggerEventState.Failed, label: 'Failed' }
    ]),
    new OptionFilter<TriggerExecutionFilter>(
      'type',
      BoltIcon,
      'Type is',
      EVENT_TYPES.map((t) => ({ value: t, label: EVENT_TYPE_LABELS[t] }))
    ),
    new BaseDateFilter<TriggerExecutionFilter>('date'),
    new BaseSearchFilter<TriggerExecutionFilter>('search')
  ];

/** What the execution filters read from a row: a trigger event or a mailbox run. */
export type FilterableExecution = Pick<
  TriggerEventListFieldsFragment,
  'state' | 'eventType' | 'receivedAt'
>;

export function filterTriggerExecutions<T extends FilterableExecution>(
  events: T[],
  activeFilters: ActiveFilter<TriggerExecutionFilter>[],
  searchText: (event: T) => string
): T[] {
  if (activeFilters.length === 0) return events;
  return events.filter((event) =>
    activeFilters.every((filter) => {
      switch (filter.type) {
        case 'state':
          return event.state === filter.value;
        case 'type':
          return event.eventType === filter.value;
        case 'date': {
          const since = getSinceDate(filter.value);
          return since == null || new Date(event.receivedAt) >= since;
        }
        case 'search':
          return matchesSearch(searchText(event), filter);
        default:
          return true;
      }
    })
  );
}
